/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import {
    HistoryGroupHeader,
    HistoryItem,
    HistoryOfferItem,
    HistoryOfferItemDeletedReasonEnum,
    HistoryOfferItemShortReasonEnum,
    HistoryType,
    ProgramType,
} from 'client-code/recordings';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../../config';
import { Locale, LocaleKeys } from '../../../../core/locale';
import { BrandingUiUtility } from '../../../../core/utils';

export const LIST_ROWS_ON_PAGE = 15;
export const LIST_ITEMS_MAX_COUNT = 500;

export const getRecordingOfferItemIconClassname = (
    deletedReason: HistoryOfferItemDeletedReasonEnum | undefined,
) => {
    switch (deletedReason) {
        case HistoryOfferItemDeletedReasonEnum.DeletedBecauseDiskconflict:
        case HistoryOfferItemDeletedReasonEnum.MaxRecordingsNowShowing:
        case HistoryOfferItemDeletedReasonEnum.Expired:
        case HistoryOfferItemDeletedReasonEnum.ExplicitlyDeleted:
            return 'recording-icon-deleted';

        default:
            return 'recording-icon-conflict';
    }
};

export const getRecordingItemsFromPage = (recordingItems: HistoryOfferItem[], page: number) => {
    const offset = page * LIST_ROWS_ON_PAGE;
    const items = recordingItems.slice(offset, offset + LIST_ROWS_ON_PAGE);
    const actualItems = items.filter(
        (item: HistoryOfferItem) => item.historyType === HistoryType.HistoryOfferItem,
    ) as HistoryOfferItem[];
    const actualItemsLength = items.filter(
        (item: HistoryOfferItem) => item.historyType === HistoryType.HistoryOfferItem,
    ).length;

    return { items, actualItems, actualItemsLength };
};

export const getRecordingOfferItemTitle = (item: HistoryOfferItem) => {
    return item.episodeTitle
        ? `${item.title}:  ${LEFT_QUOTE}${item.episodeTitle}${RIGHT_QUOTE}`
        : item.title;
};

export const isGroupHeader = (item: HistoryItem): item is HistoryGroupHeader => {
    return item.historyType === 'HistoryGroupHeader';
};

export const getHistoryItemsFromPage = (historyItems: HistoryItem[], page: number) => {
    const offset = page * LIST_ROWS_ON_PAGE;
    const items = historyItems.slice(offset, offset + LIST_ROWS_ON_PAGE);
    const actualItems = items.filter(
        (item: HistoryItem) => item.historyType === HistoryType.HistoryOfferItem,
    ) as HistoryOfferItem[];
    const actualItemsLength = items.filter(
        (item: HistoryItem) => item.historyType === HistoryType.HistoryOfferItem,
    ).length;

    return { items, actualItems, actualItemsLength };
};

export const mapHistoryItemToMessageKey = (item: HistoryOfferItem) => {
    let episodeType = '';
    let episodeTypePlural = '';
    let episodeTypeWithArticle = '';
    let title = '';
    let description = '';
    let preview = '';

    switch (item.programType) {
        case ProgramType.Episode:
            episodeType = Locale.getString(LocaleKeys.EPISODE);
            episodeTypePlural = Locale.getString(LocaleKeys.EPISODE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.EPISODE_WITH_ARTICLE);
            break;
        case ProgramType.Event:
            episodeType = Locale.getString(LocaleKeys.EVENT);
            episodeTypePlural = Locale.getString(LocaleKeys.EVENT_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.EVENT_WITH_ARTICLE);
            break;
        case ProgramType.Game:
            episodeType = Locale.getString(LocaleKeys.GAME);
            episodeTypePlural = Locale.getString(LocaleKeys.GAME_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.GAME_WITH_ARTICLE);
            break;
        case ProgramType.Race:
            episodeType = Locale.getString(LocaleKeys.RACE);
            episodeTypePlural = Locale.getString(LocaleKeys.RACE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.RACE_WITH_ARTICLE);
            break;
        case ProgramType.Show:
            episodeType = Locale.getString(LocaleKeys.SHOW);
            episodeTypePlural = Locale.getString(LocaleKeys.SHOW_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.SHOW_WITH_ARTICLE);
            break;
        case ProgramType.Movie:
            episodeType = Locale.getString(LocaleKeys.MOVIE);
            episodeTypePlural = Locale.getString(LocaleKeys.MOVIE_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.MOVIE_WITH_ARTICLE);
            break;
        case ProgramType.Match:
            episodeType = Locale.getString(LocaleKeys.MATCH);
            episodeTypePlural = Locale.getString(LocaleKeys.MATCH_PLURAL);
            episodeTypeWithArticle = Locale.getString(LocaleKeys.MATCH_WITH_ARTICLE);
            break;
        default:
    }

    switch (item.deletedReason) {
        case HistoryOfferItemDeletedReasonEnum.NotFirstRun:
            title = Locale.getString(LocaleKeys.NOT_FIRST_RUN_TITLE);
            description = Locale.getString(LocaleKeys.NOT_FIRST_RUN_BODY);
            preview = Locale.getString(LocaleKeys.NOT_FIRST_RUN_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.RecordDifferentShowing:
            title = Locale.getString(LocaleKeys.RECORDING_ACTIVITY_DIFFERENT_SHOWING_TITLE);
            description = Locale.getString(LocaleKeys.RECORDING_ACTIVITY_DIFFERENT_SHOWING_BODY);
            preview = Locale.getString(LocaleKeys.RECORDING_ACTIVITY_DIFFERENT_SHOWING_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.CancelBecauseDiskconflict:
            title = Locale.getString(LocaleKeys.CANCEL_BECAUSE_DISKCONFLICT_TITLE);
            description = Locale.getString(LocaleKeys.CANCEL_BECAUSE_DISKCONFLICT_BODY);
            preview = Locale.getString(LocaleKeys.CANCEL_BECAUSE_DISKCONFLICT_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.DeletedBecauseDiskconflict:
            title = Locale.getString(LocaleKeys.DELETED_BECAUSE_DISKCONFLICT_TITLE);
            description = Locale.getString(LocaleKeys.DELETED_BECAUSE_DISKCONFLICT_BODY);
            preview = Locale.getString(LocaleKeys.DELETED_BECAUSE_DISKCONFLICT_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.CancelMaxRecordingsExceeded:
            title = Locale.getString(LocaleKeys.CANCEL_MAX_RECORDINGS_EXCEEDED_TITLE);
            description = Locale.getString(LocaleKeys.CANCEL_MAX_RECORDINGS_EXCEEDED_BODY);
            preview = Locale.getString(LocaleKeys.CANCEL_MAX_RECORDINGS_EXCEEDED_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.MaxRecordingsNowShowing:
            title = Locale.getString(LocaleKeys.MAX_RECORDINGS_NOW_SHOWING_TITLE);
            description = Locale.getString(LocaleKeys.MAX_RECORDINGS_NOW_SHOWING_BODY);
            preview = Locale.getString(LocaleKeys.MAX_RECORDINGS_NOW_SHOWING_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.InternalError:
            title = Locale.getString(LocaleKeys.INTERNAL_ERROR_TITLE);
            description = Locale.getString(LocaleKeys.INTERNAL_ERROR_BODY);
            preview = Locale.getString(LocaleKeys.INTERNAL_ERROR_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.NotAuthorized:
            title = Locale.getString(LocaleKeys.NOT_AUTHORIZED_RECORDING_ACTIVITY_TITLE);
            description = Locale.getString(LocaleKeys.NOT_AUTHORIZED_RECORDING_ACTIVITY_BODY);
            preview = Locale.getString(LocaleKeys.NOT_AUTHORIZED_RECORDING_ACTIVITY_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.Expired:
            title = Locale.getString(LocaleKeys.EXPIRED_TITLE);
            description = Locale.getString(LocaleKeys.EXPIRED_BODY);
            preview = Locale.getString(LocaleKeys.EXPIRED_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.ExplicitlyDeleted:
            title = Locale.getString(LocaleKeys.EXPLICITLY_DELETED_TITLE);
            description = Locale.getString(LocaleKeys.EXPLICITLY_DELETED_BODY);
            preview = Locale.getString(LocaleKeys.EXPLICITLY_DELETED_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.WrongResolution:
            title = Locale.getString(LocaleKeys.WRONG_RESOLUTION_TITLE);
            description = Locale.getString(LocaleKeys.WRONG_RESOLUTION_BODY);
            preview = Locale.getString(LocaleKeys.WRONG_RESOLUTION_PREVIEW);
            break;
        case HistoryOfferItemDeletedReasonEnum.GenericUnknown:
            title = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_TITLE);
            description = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_BODY);
            preview = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_PREVIEW);
            break;
        default:
            title = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_TITLE);
            description = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_BODY);
            preview = Locale.getString(LocaleKeys.GENERIC_UNKNOWN_RECORDING_ACTIVITY_PREVIEW);
            break;
    }

    description = BrandingUiUtility.formatDescription(description, {
        EPISODE_TYPE: episodeType,
        EPISODE_TYPE_PLURAL: episodeTypePlural,
        EPISODE_TYPE_WITH_ARTICLE: episodeTypeWithArticle,
        TITLE: item.title,
    });

    return { title, description, preview };
};

export const getRecordingOfferItemReason = (shortReason: HistoryOfferItemShortReasonEnum) => {
    switch (shortReason) {
        case HistoryOfferItemShortReasonEnum.Canceled:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_CANCELED);
        case HistoryOfferItemShortReasonEnum.Canrecover:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_CAN_RECOVER);
        case HistoryOfferItemShortReasonEnum.Deleted:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_DELETED);
        case HistoryOfferItemShortReasonEnum.Duplicate:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_DUPLICATE);
        case HistoryOfferItemShortReasonEnum.Limitreached:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_LIMIT_REACHED);
        case HistoryOfferItemShortReasonEnum.Notavailable:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_NOT_AVAILABLE);
        case HistoryOfferItemShortReasonEnum.Notnew:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_NOT_NEW);
        case HistoryOfferItemShortReasonEnum.Notrecorded:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_NOT_RECORDED);
        case HistoryOfferItemShortReasonEnum.QuotaLimits:
            return Locale.getString(LocaleKeys.RECORDING_ACTIVITY_REASON_QUOTA_LIMITS);
        default:
            return '';
    }
};
